import axios from 'axios';
import { MATH_API } from '../report/mathReportApi';
import type { MathColumn } from '../report/mathReportApi';

/**
 * Набор вызовов справочника полей. Один экран («Мат. отчет - Отображение») и вкладка «Таблицы и справочники»
 * правят одну таблицу, но у каждого свои права и свой путь на сервере, поэтому экран получает вызовы параметром.
 */
export interface FieldsApi {
  list: () => Promise<MathColumn[]>;
  /** Возвращает предупреждение, если поле добавлено, но колонки с таким именем нет в таблице отчета; иначе пустую строку */
  create: (draft: MathColumn) => Promise<string>;
  update: (draft: MathColumn) => Promise<void>;
  remove: (field: string) => Promise<void>;
}

/** Права на действия со справочником; на каждом экране свои коды */
export interface FieldsPerms {
  create: string;
  edit: string;
  delete: string;
}

interface FieldDto {
  field: string;
  headerName: string;
  editable?: boolean | number;
}

interface CreateResponse {
  columnMissing?: boolean;
}

function toColumns(data: unknown): MathColumn[] {
  const list = Array.isArray(data) ? (data as FieldDto[]) : [];
  return list.map((item) => ({ field: item.field, headerName: item.headerName, editable: Boolean(item.editable) }));
}

function columnWarning(field: string): string {
  return `Поле добавлено, но в таблице мат. отчета нет колонки «${field}»: в отчете оно работать не будет`;
}

/**
 * Реализация для путей с корнем root: поля лежат по root/fields (мат. отчет, ответ - голый массив)
 * или по root/math-fields (раздел «Таблицы», ответ {success, data})
 */
function createFieldsApi(root: string, path: string, wrapped: boolean): FieldsApi {
  const base = `${root}/${path}`;
  return {
    async list() {
      const res = await axios.get<unknown>(base);
      const data = wrapped ? (res.data as { data?: unknown } | undefined)?.data : res.data;
      return toColumns(data);
    },
    async create(draft) {
      const res = await axios.post<CreateResponse>(base, {
        field: draft.field.trim(),
        headerName: draft.headerName.trim(),
        editable: draft.editable
      });
      return res.data && res.data.columnMissing ? columnWarning(draft.field.trim()) : '';
    },
    async update(draft) {
      await axios.put(`${base}/${encodeURIComponent(draft.field)}`, {
        headerName: draft.headerName.trim(),
        editable: draft.editable
      });
    },
    async remove(field) {
      await axios.delete(`${base}/${encodeURIComponent(field)}`);
    }
  };
}

/** Вкладка «Мат. отчет - Отображение»; список читает тот же вызов, что и вкладка «Отчет» */
export const MATH_FIELDS_API: FieldsApi = createFieldsApi(MATH_API, 'fields', false);

/** Вкладка «Таблицы и справочники - Поля в мат. отчете» */
export const TABLES_FIELDS_API: FieldsApi = createFieldsApi('/api/v2/tables', 'math-fields', true);

export const MATH_FIELDS_PERMS: FieldsPerms = {
  create: 'reports.math.fields.create',
  edit: 'reports.math.fields.edit',
  delete: 'reports.math.fields.delete'
};

export const TABLES_FIELDS_PERMS: FieldsPerms = {
  create: 'tables.math_fields.create',
  edit: 'tables.math_fields.edit',
  delete: 'tables.math_fields.delete'
};
